import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

import {
  getMarginStyles,
  type MarginValue,
} from "@/components/utils/marginUtils";
import type { Spacing } from "@/styles/tokens";

type StackAlign = "start" | "center" | "end" | "stretch";

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "style"> {
  children?: ReactNode;
  gap?: Spacing[keyof Spacing];
  align?: StackAlign;
  padding?: Spacing[keyof Spacing];
  margin?: MarginValue;
  className?: string;
}

const alignMap: Record<StackAlign, CSSProperties["alignItems"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
};

/**
 * Stack - Layout component for vertical flexbox
 *
 * @example
 * ```tsx
 * import { spacing } from '@/styles/tokens';
 *
 * // Margin en todos los lados
 * <Stack gap={spacing.md} margin={spacing.lg}>
 *   <div>Item 1</div>
 * </Stack>
 *
 * // Margin solo en lados específicos
 * <Stack gap={spacing.md} margin={{ top: spacing.xl, bottom: spacing.sm }}>
 *   <div>Item 1</div>
 * </Stack>
 * ```
 */
export const Stack = ({
  children,
  gap,
  align = "stretch",
  padding,
  margin,
  className,
  ...props
}: Props) => {
  const style: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    ...(gap !== undefined && { gap: `${gap}px` }),
    alignItems: alignMap[align],
    ...(padding !== undefined && { padding: `${padding}px` }),
    ...getMarginStyles(margin),
  };

  return (
    <div style={style} className={className} {...props}>
      {children}
    </div>
  );
};
